// @ts-nocheck
/**
 * Ludipek B2B per-location stock display.
 *
 * On PDPs that include a <details data-stock-by-location data-netsuite-id="..."> widget,
 * fetches /products/inventory/by-location and fills the inner <ul> with one row per store, each
 * showing a traffic light (alto / medio / bajo / sin stock), never a unit count.
 *
 * Per-market gating: a Chilean visitor (storeCountry=CL) sees SCL/CCP/ZCO; a Peruvian visitor
 * sees PER. Stores from the other market are filtered out — cross-market visibility isn't
 * useful (a Chilean customer can't ship from PER).
 *
 * Render-state lifecycle:
 *   - Liquid renders the wrapper with a single "Cargando…" placeholder <li>.
 *   - On DOMContentLoaded we fetch and replace the placeholder with the populated list.
 *   - On fetch failure we drop the placeholder entirely (the disclosure remains, but empty
 *     — the customer falls back to the aggregate badge above).
 *
 * The same answer carries each branch's earliest Fecha corta expiry (include_expiry), which
 * replaces the SSR "Vence …" line of the Liquidación option with the active branch's own date.
 */
(function () {
  const BOOTSTRAP = window.__LUDIPEK_B2B__ || null;
  const FETCH_TIMEOUT_MS = 10_000;

  // Map storeCountry (ISO alpha-2 from Shopify localization) to the store_codes that belong to
  // that market. SCL/CCP/ZCO are Chilean; PER/TRU/PIU are Peruvian. Any market not listed
  // defaults to showing every store_code (e.g. an admin preview, or an as-yet-unmapped country).
  // The product cards' badge labels (ludipek-stock-badge.liquid), except that a branch with none reads
  // "Sin stock": the product may still be bought from another branch.
  const STOCK_LEVEL_LABELS = {
    alto: 'Stock alto',
    medio: 'Stock medio',
    bajo: 'Stock bajo',
    agotado: 'Sin stock',
  };

  const STORES_BY_MARKET = {
    CL: ['SCL', 'CCP', 'ZCO'],
    PE: ['PER', 'TRU', 'PIU'],
  };

  function logDebug(message, details) {
    if (details === undefined) {
      console.debug('[b2b-stock-by-location]', message);
    } else {
      console.debug('[b2b-stock-by-location]', message, details);
    }
  }

  function logWarn(message, details) {
    if (details === undefined) {
      console.warn('[b2b-stock-by-location]', message);
    } else {
      console.warn('[b2b-stock-by-location]', message, details);
    }
  }

  function bootstrapIsUsable() {
    if (BOOTSTRAP === null) {
      logWarn('window.__LUDIPEK_B2B__ not present — skipping per-location stock');
      return false;
    }
    if (!BOOTSTRAP.apiBaseUrl) {
      logWarn('apiBaseUrl is empty — set "Ludipek API base URL" in theme settings');
      return false;
    }
    return true;
  }

  function getEffectiveCountry() {
    if (window.LudipekB2BLocation && window.LudipekB2BLocation.getEffectiveCountry) {
      return window.LudipekB2BLocation.getEffectiveCountry();
    }
    return (BOOTSTRAP && BOOTSTRAP.storeCountry) || null;
  }

  function visibleStoreCodesForMarket() {
    const country = getEffectiveCountry();
    if (country && STORES_BY_MARKET[country]) {
      return STORES_BY_MARKET[country];
    }
    return null;
  }

  // The buyer's active branch (store_code), or null before a location is chosen.
  function getActiveStoreCode() {
    if (window.LudipekB2BLocation && window.LudipekB2BLocation.getStoreCode) {
      return window.LudipekB2BLocation.getStoreCode();
    }
    return null;
  }

  // Buyer-facing city of the active branch, for the PDP's "Sin stock … en [sucursal]" reasons: the
  // CompanyLocation name the selector stored (accented, e.g. "Concepción"), else the API's store name
  // for a legacy location still named by its store_code.
  function activeBranchName(storeCode, activeStore) {
    const location = window.LudipekB2BLocation && window.LudipekB2BLocation.getLocation
      ? window.LudipekB2BLocation.getLocation()
      : null;
    const locationName = location && location.store_name;
    if (locationName && locationName !== storeCode) return locationName;
    return (activeStore && activeStore.name) || '';
  }

  async function fetchByLocation(productIds) {
    const params = new URLSearchParams();
    productIds.forEach((id) => params.append('product_ids', String(id)));
    // Each branch's own Fecha corta expiry, for the Liquidación option (writeBranchExpiry). Cards never ask.
    params.append('include_expiry', 'true');
    const url = `${BOOTSTRAP.apiBaseUrl.replace(/\/$/, '')}/products/inventory/by-location?${params.toString()}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          'x-api-key': BOOTSTRAP.apiShopifyKey,
        },
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // A traffic light for one store's stock, on the product cards' buckets and labels (ludipek-stock-badge.liquid,
  // b2b-card-stock.js). Never a unit count: Ludipek does not show stock units on the storefront.
  function stockLight(quantity) {
    let level = 'agotado';
    if (quantity > 50) level = 'alto';
    else if (quantity > 10) level = 'medio';
    else if (quantity > 0) level = 'bajo';
    const light = document.createElement('span');
    light.className = `lp-stock lp-stock--${level}`;
    light.setAttribute('data-level', level);
    const dot = document.createElement('span');
    dot.className = 'lp-stock__dot';
    dot.setAttribute('aria-hidden', 'true');
    light.append(dot, STOCK_LEVEL_LABELS[level]);
    return light;
  }

  // Render one <tr> per store inside the <tbody>, each cell a traffic light. The Fecha-corta column
  // only shows when at least one store has FC stock — otherwise the column is collapsed (its <th>
  // and per-row <td> are skipped). data-has-fc on the widget drives the column-level emphasis when
  // the PDP variant selector flips.
  function renderListItems(tbodyEl, stores, options) {
    tbodyEl.innerHTML = '';
    const widget = options && options.widget;
    const showFcColumn = stores.some((store) => (store.fecha_corta_available || 0) > 0);

    if (widget) {
      widget.setAttribute('data-has-fc', showFcColumn ? 'true' : 'false');
      const head = widget.querySelector('[data-stock-by-location-head]');
      if (head) {
        if (showFcColumn) head.removeAttribute('hidden');
        else head.setAttribute('hidden', '');
      }
    }

    stores.forEach((store) => {
      const tr = document.createElement('tr');
      const regular = store.available || 0;
      const fc = store.fecha_corta_available || 0;
      if (regular === 0 && fc === 0) tr.setAttribute('data-empty', 'true');

      const nameCell = document.createElement('th');
      nameCell.scope = 'row';
      nameCell.className = 'lp-pdp__stock-locations-name';
      nameCell.textContent = store.name;
      tr.appendChild(nameCell);

      const regularCell = document.createElement('td');
      regularCell.className = 'lp-pdp__stock-locations-qty lp-pdp__stock-locations-qty--regular';
      regularCell.appendChild(stockLight(regular));
      if (regular === 0) regularCell.setAttribute('data-empty', 'true');
      tr.appendChild(regularCell);

      if (showFcColumn) {
        const fcCell = document.createElement('td');
        fcCell.className = 'lp-pdp__stock-locations-qty lp-pdp__stock-locations-qty--fc';
        fcCell.appendChild(stockLight(fc));
        if (fc === 0) fcCell.setAttribute('data-empty', 'true');
        tr.appendChild(fcCell);
      }

      tbodyEl.appendChild(tr);
    });

    if (stores.length === 0) {
      const tr = document.createElement('tr');
      tr.setAttribute('data-empty', 'true');
      const td = document.createElement('td');
      td.colSpan = showFcColumn ? 3 : 2;
      td.textContent = 'No hay datos de stock por ubicación';
      tr.appendChild(td);
      tbodyEl.appendChild(tr);
    }
  }

  function collectWidgets(root) {
    return Array.from(root.querySelectorAll('[data-stock-by-location][data-netsuite-id]'));
  }

  async function renderForRoot(root) {
    if (!bootstrapIsUsable()) return;

    // Pending approval: stock is gated together with prices for unapproved accounts. The whole
    // widget is display:none'd via body.b2b-pending CSS; dropping the loading placeholder here
    // keeps the DOM clean and guarantees no per-bodega numbers are ever fetched or painted.
    if (BOOTSTRAP.isPending === true) {
      logDebug('customer is pending approval — per-location stock disabled');
      collectWidgets(root).forEach((widget) => {
        const loadingRow = widget.querySelector('[data-stock-by-location-loading]');
        if (loadingRow) loadingRow.remove();
      });
      return;
    }

    const widgets = collectWidgets(root);
    if (widgets.length === 0) {
      logDebug('no per-location widgets on this page');
      return;
    }

    const productIds = [];
    const widgetsByProduct = new Map();
    widgets.forEach((widget) => {
      const ns = Number(widget.getAttribute('data-netsuite-id'));
      if (!Number.isFinite(ns) || ns <= 0) return;
      productIds.push(ns);
      widgetsByProduct.set(ns, widget);
    });

    if (productIds.length === 0) return;

    let payload;
    try {
      payload = await fetchByLocation(productIds);
    } catch (error) {
      logWarn('per-location fetch failed', { error: String(error) });
      // Drop the loading placeholder so it doesn't sit there forever, and tell the PDP the stock is
      // unknown so it withdraws the Liquidación lot rather than leaving it pending.
      widgetsByProduct.forEach((widget, productId) => {
        const list = widget.querySelector('[data-stock-by-location-list]');
        if (list) list.innerHTML = '';
        document.dispatchEvent(new CustomEvent('b2b:pdp-stock', {
          detail: { productId, failed: true },
        }));
      });
      return;
    }

    const visibleCodes = visibleStoreCodesForMarket();
    const items = (payload && payload.items) || [];

    items.forEach((entry) => {
      const widget = widgetsByProduct.get(Number(entry.product_id));
      if (!widget) return;
      const list = widget.querySelector('[data-stock-by-location-list]');
      if (!list) return;

      let stores = entry.stores || [];
      if (visibleCodes !== null) {
        stores = stores.filter((store) => visibleCodes.includes(store.store_code));
      }
      renderListItems(list, stores, { widget });
      syncLotStatusFromSelector(widget);
      // Before the stock event: the PDP script enables the Liquidación option on it, and the option must
      // already carry the branch's date, not the nearest one across the country.
      writeBranchExpiry(stores);
      dispatchPdpStock(entry.product_id, stores);
    });
  }

  // "Vence …" for an ISO date (YYYY-MM-DD), in the PDP's wording: the exact date always, plus a countdown
  // within ~3 months. Days are counted between calendar dates in the buyer's timezone.
  function expiryLabel(isoDate) {
    const parts = String(isoDate).split('-').map(Number);
    if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return '';
    const [year, month, day] = parts;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.round((new Date(year, month - 1, day) - today) / 86_400_000);
    const pad = (value) => String(value).padStart(2, '0');
    const dateShort = `${pad(day)}/${pad(month)}/${year}`;
    if (days < 0) return `Vencido el ${dateShort}`;
    if (days === 0) return 'Vence hoy';
    if (days === 1) return 'Vence mañana';
    if (days <= 30) return `Vence en ${days} días · ${dateShort}`;
    if (days <= 90) {
      const months = Math.floor(days / 30);
      return `Vence en ~${months} ${months === 1 ? 'mes' : 'meses'} · ${dateShort}`;
    }
    return `Vence el ${dateShort}`;
  }

  // Short-dated stock ships only from the branch that holds it, so the Liquidación option shows the ACTIVE
  // branch's nearest expiry, not the SSR one (the nearest across every branch). An answer without the field
  // (an API older than include_expiry) leaves the SSR date; a branch whose lots carry no date hides the line.
  function writeBranchExpiry(stores) {
    const expiryLine = document.querySelector('[data-lot-status-selector] [data-fc-expires]');
    if (!expiryLine) return;
    const storeCode = getActiveStoreCode();
    const activeStore = storeCode ? stores.find((store) => store.store_code === storeCode) : null;
    if (!activeStore || !('fecha_corta_expires_at' in activeStore)) return;
    const label = activeStore.fecha_corta_expires_at ? expiryLabel(activeStore.fecha_corta_expires_at) : '';
    expiryLine.textContent = label;
    expiryLine.hidden = label === '';
  }

  // Broadcast one product's stock to the PDP inline script, which gates the lot-status selector
  // and caps the quantity input to the active variant's stock:
  //   regularTotal  — regular stock across the market (orders route across its warehouses)
  //   fcTotal       — Fecha corta stock across the market (decides whether there is a lot to pick)
  //   branchFcTotal — Fecha corta stock at the buyer's ACTIVE branch, the only FC stock they can
  //                   buy: short-dated stock ships from the branch that holds it
  //   branchName    — the active branch's city, for the "Sin stock … en [sucursal]" reasons
  // Product cards read the same endpoint in b2b-card-stock.js — this event is PDP-only.
  function dispatchPdpStock(productId, stores) {
    const storeCode = getActiveStoreCode();
    const activeStore = storeCode ? stores.find((store) => store.store_code === storeCode) : null;
    const regularTotal = stores.reduce((sum, store) => sum + (store.available || 0), 0);
    const fcTotal = stores.reduce((sum, store) => sum + (store.fecha_corta_available || 0), 0);
    const branchFcTotal = activeStore ? activeStore.fecha_corta_available || 0 : 0;
    document.dispatchEvent(new CustomEvent('b2b:pdp-stock', {
      detail: {
        productId: Number(productId),
        regularTotal,
        fcTotal,
        branchFcTotal,
        branchName: activeBranchName(storeCode, activeStore),
      },
    }));
  }

  // PDP variant selector sets data-lot-status on its container; mirror that onto the stock
  // widget so the CSS can dim the column that's not currently being bought. Re-runs on every
  // selector change for the lifetime of the page.
  function syncLotStatusFromSelector(widget) {
    const selector = document.querySelector('[data-lot-status-selector]');
    if (!selector) return;
    const active = selector.querySelector('.lp-pdp__lot-option.is-active');
    const lotStatus = active ? active.getAttribute('data-lot-status') : 'standard';
    widget.setAttribute('data-active-lot-status', lotStatus || 'standard');
  }

  document.addEventListener('click', (event) => {
    const button = event.target && event.target.closest
      ? event.target.closest('.lp-pdp__lot-option')
      : null;
    // A disabled option (no stock, or Liquidación still pending) selects nothing, so it must not
    // re-emphasise the table either.
    if (!button || button.disabled) return;
    const lotStatus = button.getAttribute('data-lot-status') || 'standard';
    document.querySelectorAll('[data-stock-by-location]').forEach((widget) => {
      widget.setAttribute('data-active-lot-status', lotStatus);
    });
  });

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  onReady(() => {
    renderForRoot(document);
  });

  // If a section reload fires (admin theme editor, dynamic re-renders), refresh the widget.
  document.addEventListener('section:reload', (event) => {
    const target = event && event.target && event.target.nodeType === 1 ? event.target : document;
    renderForRoot(target);
  });

  window.LudipekB2BStockByLocation = { renderForRoot };
})();
